import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";

export type ThemeMode = "light" | "dark";
type ThemePreference = ThemeMode | "system";
const STORAGE_KEY = "salesapp:theme-mode";

function readPreference(): ThemePreference {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}

function systemMode(): ThemeMode {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
}

interface ThemeContextValue {
  mode: ThemeMode;
  preference: ThemePreference;
  setMode: (mode: ThemeMode) => void;
  setPreference: (mode: ThemePreference) => void;
  getRevision: () => number;
}
const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, updatePreference] = useState<ThemePreference>(readPreference);
  const [osMode, setOsMode] = useState<ThemeMode>(systemMode);
  const revision = useRef(0);
  const getRevision = useCallback(() => revision.current, []);
  const mode = preference === "system" ? osMode : preference;

  const apply = useCallback((next: ThemeMode) => {
    document.documentElement.dataset.theme = next;
    document.documentElement.style.colorScheme = next;
  }, []);

  useLayoutEffect(() => {
    apply(mode);
  }, [apply, mode]);
  useEffect(() => {
    if (preference !== "system") return;
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!media?.addEventListener) return;
    const onChange = () => setOsMode(systemMode());
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [apply, preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    revision.current += 1;
    try {
      if (next === "system") window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, next);
    } catch { /* storage can be unavailable */ }
    setOsMode(systemMode());
    updatePreference(next);
  }, []);
  const setMode = setPreference;
  const value = useMemo(
    () => ({ mode, preference, setMode, setPreference, getRevision }),
    [mode, preference, setMode, setPreference, getRevision],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("useTheme() must be used within ThemeProvider");
  return value;
}
